import { useEffect, useRef, useState } from "react";
import { navigate } from "../lib/router";
import { ago } from "../lib/markdown";
import { useNotifications } from "../lib/useNotifications";
import type { HubNotification, NotificationAction, NotificationKind } from "../types";
import {
  NOTIFICATION_ACTION_LABELS,
  NOTIFICATION_BUCKETS,
  notificationActionsFor,
  notificationNeedsConfirm,
} from "../types";

// The global top-bar bell: an unread count + a dropdown of the caller's own
// notifications, spanning every Omni Hub source (DevHub review, session
// share/activity, task-done). Clicking one navigates to the event's place in the
// app (the notification's `href`) and marks it read. Delivery is per-user — the
// server only ever returns the caller's own — so no other person's activity leaks.

// A small glyph per notification kind, drawn in the topbar icon stroke style.
function KindIcon({ kind }: { kind: NotificationKind }) {
  const common = {
    viewBox: "0 0 24 24",
    width: 16,
    height: 16,
    fill: "none",
    stroke: "currentColor",
    strokeWidth: 1.7,
    strokeLinecap: "round" as const,
    strokeLinejoin: "round" as const,
    "aria-hidden": true,
  };
  switch (kind) {
    case "session_shared":
      // people / share
      return (
        <svg {...common}><circle cx="9" cy="8" r="3.2" /><path d="M3.5 20a5.5 5.5 0 0 1 11 0" /><path d="M17 5l3 3-3 3" /><path d="M20 8h-6" /></svg>
      );
    case "session_activity":
      // chat bubble
      return (
        <svg {...common}><path d="M21 15a2 2 0 0 1-2 2H8l-4 4V5a2 2 0 0 1 2-2h11a2 2 0 0 1 2 2z" /></svg>
      );
    case "task_done":
      // check circle
      return (
        <svg {...common}><circle cx="12" cy="12" r="9" /><path d="M8.5 12.5l2.2 2.2 4.8-5" /></svg>
      );
    case "devhub_sdk_release":
      // package / box — an SDK release, not a page. Its own glyph because these
      // arrive alongside review notifications and the two are acted on in
      // different tabs (ENG-56744).
      return (
        <svg {...common}><path d="M21 8v8a2 2 0 0 1-1 1.73l-7 4a2 2 0 0 1-2 0l-7-4A2 2 0 0 1 3 16V8a2 2 0 0 1 1-1.73l7-4a2 2 0 0 1 2 0l7 4A2 2 0 0 1 21 8z" /><path d="M3.3 7.3 12 12l8.7-4.7" /><path d="M12 12v9.5" /></svg>
      );
    case "devhub_sdk_review_pr":
      // pull request — a branch merging back. Distinct from the package above
      // because the two arrive about the same release and are acted on at
      // opposite ends of it: one says review this, the other says publish it.
      return (
        <svg {...common}><circle cx="6.5" cy="5.5" r="2.5" /><circle cx="6.5" cy="18.5" r="2.5" /><circle cx="17.5" cy="18.5" r="2.5" /><path d="M6.5 8v8" /><path d="M17.5 16V9a3 3 0 0 0-3-3h-3" /><path d="M13.5 3.5 11 6l2.5 2.5" /></svg>
      );
    case "devhub_publish_late":
      // Clock — a publish that merged and has not reached readers. Its own
      // glyph because it arrives beside "Published" and says the opposite
      // thing about the same page (ENG-57566).
      return (
        <svg {...common}><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" /></svg>
      );
    case "devhub_review":
    default:
      // document
      return (
        <svg {...common}><path d="M6 2h8l4 4v16H6z" /><path d="M14 2v4h4" /><path d="M9 13h6M9 17h6" /></svg>
      );
  }
}

export default function NotificationBell() {
  const { items, counts, unread, bucket, setBucket, markRead, markAllRead, apply } = useNotifications();
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState<Set<string>>(() => new Set());
  const [problem, setProblem] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  // Close on click-away (matches the presence popup / other dropdowns).
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [open]);

  // A selection belongs to the tab it was made in: the ids in another tab are a
  // different set of rows, and carrying them across would apply an action to
  // things the reader can no longer see. Cleared on a tab change and when the
  // menu closes, which is the same rule stated once.
  useEffect(() => { setSelected(new Set()); setProblem(null); }, [bucket, open]);

  const onOpenNotification = (n: HubNotification) => {
    setOpen(false);
    void markRead(n.id);
    navigate(n.href);
  };

  const toggle = (id: string) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });

  const allSelected = items.length > 0 && items.every((n) => selected.has(n.id));

  const run = async (ids: string[], action: NotificationAction) => {
    if (!ids.length) return;
    // A bulk delete is the one action that cannot be undone and the one that
    // confirms — a single delete does not, per the ticket's own split.
    if (notificationNeedsConfirm(action, ids.length)
      && !window.confirm(`Delete ${ids.length} notifications? This cannot be undone.`)) return;
    setBusy(true);
    setProblem(null);
    const missing = await apply(ids, action);
    setBusy(false);
    // Selection clears once the action completes — it does not silently persist
    // onto rows that have moved tab.
    setSelected(new Set());
    if (missing.length) {
      setProblem(`${missing.length} notification${missing.length === 1 ? "" : "s"} could not be updated.`);
    }
  };

  return (
    <div className="oh-bell" ref={ref}>
      <button
        type="button"
        className={`theme-toggle oh-bell-btn${unread > 0 ? " has-unread" : ""}`}
        aria-label={unread > 0 ? `Notifications (${unread} unread)` : "Notifications"}
        aria-expanded={open}
        title="Notifications"
        onClick={() => setOpen((v) => !v)}
      >
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M18 8a6 6 0 0 0-12 0c0 7-3 9-3 9h18s-3-2-3-9" /><path d="M13.7 21a2 2 0 0 1-3.4 0" /></svg>
        {unread > 0 && <span className="oh-bell-badge">{unread > 9 ? "9+" : unread}</span>}
      </button>

      {open && (
        <div className="oh-bell-menu" role="menu">
          <div className="oh-bell-head">
            <span>Notifications</span>
            {bucket === "unread" && unread > 0 && (
              <button type="button" className="oh-bell-mark-all" onClick={() => void markAllRead()}>
                Mark all read
              </button>
            )}
          </div>

          {/* Three tabs, in the shared vocabulary the store filters by and the
              route validates against. Archived carries its count like the others
              so an inbox that has been filed away still says how much is in it. */}
          <div className="oh-bell-tabs" role="tablist">
            {NOTIFICATION_BUCKETS.map((b) => (
              <button
                key={b.key}
                type="button"
                role="tab"
                aria-selected={b.key === bucket}
                className={`oh-bell-tab${b.key === bucket ? " active" : ""}`}
                onClick={() => setBucket(b.key)}
              >
                {b.label} <span className="oh-bell-tab-count">{counts[b.key]}</span>
              </button>
            ))}
          </div>

          {items.length > 0 && (
            <div className="oh-bell-bulk">
              <label className="oh-bell-selectall">
                <input
                  type="checkbox"
                  checked={allSelected}
                  onChange={() => setSelected(allSelected ? new Set() : new Set(items.map((n) => n.id)))}
                />
                {selected.size > 0 ? `${selected.size} selected` : "Select all"}
              </label>
              {selected.size > 0 && (
                <span className="oh-bell-bulk-actions">
                  {/* The SAME action list the per-row buttons use, so a bulk
                      action can never be one the tab does not offer. */}
                  {notificationActionsFor(bucket).map((action) => (
                    <button
                      key={action}
                      type="button"
                      className={`oh-bell-action${action === "delete" ? " danger" : ""}`}
                      disabled={busy}
                      onClick={() => void run([...selected], action)}
                    >
                      {NOTIFICATION_ACTION_LABELS[action]}
                    </button>
                  ))}
                </span>
              )}
            </div>
          )}

          {problem && <div className="oh-bell-problem" role="alert">{problem}</div>}

          {items.length === 0 ? (
            <div className="oh-bell-empty">
              {bucket === "unread" ? "You're all caught up."
                : bucket === "read" ? "Nothing read yet."
                : "Nothing archived."}
            </div>
          ) : (
            <ul className="oh-bell-list">
              {items.map((n) => (
                <li
                  key={n.id}
                  className={[selected.has(n.id) && "selected", !n.readAt && "unread"].filter(Boolean).join(" ") || undefined}
                >
                  <input
                    type="checkbox"
                    className="oh-bell-check"
                    checked={selected.has(n.id)}
                    onChange={() => toggle(n.id)}
                    aria-label={`Select “${n.title}”`}
                  />
                  <button
                    type="button"
                    className="oh-bell-item"
                    onClick={() => onOpenNotification(n)}
                  >
                    <span className="oh-bell-item-icon"><KindIcon kind={n.kind} /></span>
                    <span className="oh-bell-item-main">
                      <span className="oh-bell-item-top">
                        <span className="oh-bell-item-title">{n.title}</span>
                        <span className="oh-bell-item-time">{ago(n.createdAt)}</span>
                      </span>
                      {n.body && <span className="oh-bell-item-body">{n.body}</span>}
                      {n.fromUser && <span className="oh-bell-item-from">{n.fromUser}</span>}
                    </span>
                  </button>
                  <span className="oh-bell-item-actions">
                    {notificationActionsFor(bucket).map((action) => (
                      <button
                        key={action}
                        type="button"
                        className={`oh-bell-action${action === "delete" ? " danger" : ""}`}
                        disabled={busy}
                        title={NOTIFICATION_ACTION_LABELS[action]}
                        onClick={() => void run([n.id], action)}
                      >
                        {NOTIFICATION_ACTION_LABELS[action]}
                      </button>
                    ))}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
